import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    withStyles,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { Link } from 'react-router-dom';
import { adminMenuStyles } from './styles';

import organisationTabs from '../header/tabsReferences/organisationTabs';
import resourcesTabs from '../header/tabsReferences/resourcesTabs';

import LoadingScreen from '../../collums-components/components/common/loadingScreen';
import AuthApi from '../../collums-components/api/AuthApi';
import { toastr } from 'react-redux-toastr';
import { ROLES } from '../../collums-constants';
import locationTabs from '../header/tabsReferences/locationTabs';
import { useDispatch, useSelector } from 'react-redux';
import { loadOrganisation } from '../../actions/organisationActions';
import { listClinics } from '../../collums-components/api/ClinicApi';
import OrganisationsApi from '../../api/OrganisationsApi';
import api from '../../collums-components/api/config';
import practitionerApi from '../../api/PractitionerApi';

const MenuOption = ({ classes, option }) => {
    const links = option.links;

    return (
        <Grid item className={classes.menuOption}>
            <Typography variant="h3" style={{ textAlign: 'center' }}>
                {option.name}
            </Typography>
            <Grid container spacing={0} className={classes.menuOptionContainer}>
                {links.length >= 5 ? (
                    <>
                        <Grid item md={6} className={classes.menuOptionItem}>
                            {/* ---- Check if is a odd number of options to add the top of next column at the bottom*/}
                            {links
                                .slice(0, links.length % 2 !== 0 ? (links.length / 2).toFixed(0) : links.length / 2)
                                .map(link =>
                                    link?.locked ? (
                                        <Typography variant="span" className={classes.linkLocked}>
                                            {link.name}
                                        </Typography>
                                    ) : (
                                        <Link key={link.name} to={link.ref} className={classes.link}>
                                            {link.name}
                                        </Link>
                                    )
                                )}
                        </Grid>
                        <Grid item md={6} className={classes.menuOptionItem}>
                            {/* ---- Check if is a odd number of options to remove the first one of the column */}
                            {links.slice(links.length % 2 !== 0 ? links.length / 2 + 1 : links.length / 2).map(link =>
                                link?.locked ? (
                                    <Typography variant="span" className={classes.linkLocked}>
                                        {link.name}
                                    </Typography>
                                ) : (
                                    <Link key={link.name} to={link.ref} className={classes.link}>
                                        {link.name}
                                    </Link>
                                )
                            )}
                        </Grid>
                    </>
                ) : (
                    <Grid item md={12} className={classes.menuOptionItem}>
                        {links.map(link =>
                            link?.locked ? (
                                <Typography variant="span" className={classes.linkLocked}>
                                    {link.name}
                                </Typography>
                            ) : (
                                <Link
                                    key={link.name}
                                    to={link.ref}
                                    className={`${classes.link} ${link?.locked ? 'locked' : ''}`}
                                >
                                    {link.name}
                                </Link>
                            )
                        )}
                    </Grid>
                )}
            </Grid>
            {!links.length && option.name === 'Location Settings' && <p>No locations available.</p>}
        </Grid>
    );
};

const AdminMenu = ({ classes }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showSpecialPanel, setShowSpecialPanel] = useState(false);
    const [clinics, setClinics] = useState([]);
    const organisation = useSelector(state => state.organisation);
    const [selectedClinic, setSelectedClinic] = useState(organisation.clinic);
    const [organisationData, setOrganisationData] = useState(null);
    const [activePractitioners, setActivePractitioners] = useState([]);

    useEffect(() => {
        getCurrentUserClincs();
        getActivePractitioners();
    }, []); //eslint-disable-line

    const dispatch = useDispatch();

    useEffect(() => {
        (async () => {
            const organisation = await OrganisationsApi.getOrganisation();
            setOrganisationData(organisation);
        })();
    }, []);

    async function getCurrentUserClincs() {
        try {
            setIsLoading(true);
            const currentUser = await AuthApi.getMe();
            if (['developer@collums.co.uk', 'at@collums.co'].includes(currentUser.email.address)) {
                setShowSpecialPanel(true);
            }
            const isPractitionerAdmin = currentUser.role === ROLES.ADMIN || currentUser.job.name === ROLES.ADMIN;
            const practitionerClinics = await listClinics();
            const clinics = (practitionerClinics || []).map(clinic => ({
                id: clinic.id,
                name: clinic.accountName
            }));
            const clinicOptions =
                isPractitionerAdmin || clinics.length > 1
                    ? [{ id: 'organisation', name: 'Organisation' }, ...clinics]
                    : clinics;
            setClinics(clinicOptions);
            setSelectedClinic(organisation.clinic || clinicOptions[0]);
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            setIsLoading(false);
        }
    }

    async function getActivePractitioners() {
        setActivePractitioners(await practitionerApi.listActives());
    }

    const clinicLocationTabs = (clinicId, isSingleClinic = false) => {
        const choosenClinicId = clinicId || selectedClinic?.id;
        const choosenClinic = activePractitioners.find(loc => loc.id === choosenClinicId);
        let haveSoloPractitioner = false;
        if (choosenClinic) {
            haveSoloPractitioner = choosenClinic.practitioners.find(staff => {
                return !!staff.locations.find(loc => loc.clinic === choosenClinicId && loc.isSoloPractitioner);
            });
        }
        return locationTabs
            .map(tab => {
                if (isSingleClinic && ['Staff', 'Alerts', 'Discounts'].includes(tab.name)) {
                    return false;
                }
                if (haveSoloPractitioner && tab.name === 'Opening Hours') {
                    return false;
                }
                return {
                    ...tab,
                    ref: choosenClinicId ? tab.ref.replace('/location', `/location/${choosenClinicId}`) : tab.ref
                };
            })
            .filter(tab => !!tab);
    };

    const organisationMenu = singleClinicId => {
        if (!organisationData) {
            return [];
        }

        const menu = [];
        organisationTabs.map(tab => {
            if (singleClinicId) {
                if (tab.ref === '/organisation/alerts') {
                    tab.ref = `/location/${singleClinicId}/alerts`;
                }
            }

            menu.push(tab);
            return true;
        });
        return menu;
    };

    const options = [];

    if (clinics.filter(clinic => clinic.name !== 'Organisation').length > 1) {
        options.push(
            {
                name: `${selectedClinic?.name || ''} Resources`,
                links: resourcesTabs(selectedClinic?.id)
            },
            {
                name: `${selectedClinic?.name || ''} Settings`,
                links: clinicLocationTabs()
            }
        );
        if (!selectedClinic || selectedClinic?.name === 'Organisation') {
            options.pop();
            if (selectedClinic?.name !== 'Organisation') {
                options.pop();
            }
        }

        if (selectedClinic?.name === 'Organisation') {
            options.push({
                name: 'Organisation Settings',
                links: organisationMenu()
            });
        }
    } else {
        let singleClinicId = null;
        // eslint-disable-next-line
        clinics.map(clinic => {
            if (clinic?.name !== 'Organisation') {
                const clinicsWithoutOrganisation = clinics.filter(clinic => clinic.id !== 'organisation');
                options.push(
                    {
                        name: `${clinic?.name || ''} Resources`,
                        links: resourcesTabs(clinic?.id, clinicsWithoutOrganisation.length === 1)
                    },
                    {
                        name: `${clinic?.name || ''} Settings`,
                        links: clinicLocationTabs(clinic?.id, true)
                    }
                );
                singleClinicId = clinic?.id;
            }
        });
        options.push({
            name: 'Organisation Settings',
            links: organisationMenu(singleClinicId)
        });
    }

    const _filterOptions = createFilterOptions();

    const filterOptions = (options, state) => {
        const results = _filterOptions(options, state);
        if (clinics?.[0]?.name === 'Organisation') {
            if (!results.includes(clinics[0])) {
                results.unshift(clinics[0]);
            }
        }

        return results;
    };

    return (
        <>
            <div className={classes.headerContainer}>
                <Typography variant="h2" className={classes.adminTitle}>
                    {selectedClinic?.name} Admin Menu
                </Typography>
                {clinics.filter(clinic => clinic.name !== 'Organisation').length > 1 && (
                    <Autocomplete
                        className={`${classes.clinicSelector} ${classes.font}`}
                        fullWidth
                        autoComplete
                        autoHighlight
                        margin="dense"
                        // value={selectedClinic && selectedClinic.name} --- insert default selected
                        defaultValue={selectedClinic && selectedClinic}
                        options={clinics}
                        getOptionLabel={opt => opt.name}
                        onChange={(e, value) => {
                            if (value.id && value.id !== 'organisation') {
                                localStorage.setItem('currentClinic', value.id);
                            }
                            setSelectedClinic(value);
                            dispatch(loadOrganisation({ ...organisation, clinic: value }));
                        }}
                        filterOptions={filterOptions}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant={'outlined'}
                                fullWidth
                                className={`${classes.autocomplete} ${classes.clinicOptions} ${classes.font} ${classes.selectLocation} locationPlaceholder`}
                                placeholder={'Select a Location'}
                            />
                        )}
                    />
                )}
            </div>
            <Grid
                container
                direction="row"
                justify="space-evenly"
                alignItems="center"
                spacing={3}
                className={classes.adminMenuContainer}
            >
                {options.map((option, index) => (
                    <MenuOption key={index} option={option} classes={classes} />
                ))}
                {showSpecialPanel && <SpecialPanel classes={classes} />}
            </Grid>
            {isLoading && <LoadingScreen />}
        </>
    );
};

const SpecialPanel = ({ classes }) => {
    const [kpiModalOpen, setKpiModalOpen] = useState(false);
    const [kpiType, setKpiType] = useState('monthly');
    const [kpiDate, setKpiDate] = useState(new Date().toISOString().slice(0, 10));
    const [kpiLoading, setKpiLoading] = useState(false);

    const handleKpiGenerate = async () => {
        setKpiLoading(true);
        try {
            await api.request({
                method: 'GET',
                url: `/reports/kpi-test?type=${kpiType}&date=${kpiDate}`
            });
            toastr.success('KPI Report sent');
        } catch (err) {
            toastr.error('Error generating KPI Report', err.message);
        }
        setKpiLoading(false);
        setKpiModalOpen(false);
    };

    return (
        <Grid item className={classes.menuOption}>
            <Typography variant="h3" style={{ textAlign: 'center' }}>
                Special Panel
            </Typography>
            <Grid container spacing={0} className={classes.menuOptionContainer}>
                <Link
                    key="productstock"
                    className={classes.link}
                    onClick={() => {
                        return api.request({
                            method: 'GET',
                            url: '/onerunjobs/productstock'
                        });
                    }}
                >
                    Recount product stock
                </Link>
                <Link
                    key="paymentlink-notification"
                    className={classes.link}
                    onClick={() => {
                        return api.request({
                            method: 'GET',
                            url: '/onerunjobs/paymentlink-notification'
                        });
                    }}
                >
                    Payment link notification
                </Link>
                <Link
                    key="remove-db-indexes"
                    className={classes.link}
                    onClick={() => {
                        return api.request({
                            method: 'GET',
                            url: '/onerunjobs/remove-db-indexes'
                        });
                    }}
                >
                    Remove db indexes
                </Link>
                <Link
                    key="fix-appointments-index"
                    className={classes.link}
                    onClick={() => {
                        return api.request({
                            method: 'GET',
                            url: '/onerunjobs/fix-appointments-index'
                        });
                    }}
                >
                    Fix appointments index
                </Link>
                <Link key="kpi-report" className={classes.link} onClick={() => setKpiModalOpen(true)}>
                    KPI Report
                </Link>
            </Grid>
            <Dialog open={kpiModalOpen} onClose={() => setKpiModalOpen(false)}>
                <DialogTitle>Generate KPI Report</DialogTitle>
                <DialogContent>
                    <RadioGroup value={kpiType} onChange={e => setKpiType(e.target.value)} row>
                        <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                        <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                    </RadioGroup>
                    <TextField
                        type="date"
                        label="Reference date"
                        value={kpiDate}
                        onChange={e => setKpiDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        style={{ marginTop: 16 }}
                        helperText={
                            kpiType === 'monthly'
                                ? 'Report will cover the month containing this date'
                                : 'Report will cover the week (Mon-Sun) containing this date'
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKpiModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleKpiGenerate} color="primary" variant="contained" disabled={kpiLoading}>
                        {kpiLoading ? 'Generating...' : 'Generate & Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default withStyles(adminMenuStyles)(AdminMenu);
